import { Bell, LogOut, Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserAvatar } from '@/components/common/user-avatar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/stores/auth-store';

export function TopBar() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const setActiveOrgId = useAuthStore((s) => s.setActiveOrgId);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const { success } = useToast();

  const handleOrgSwitch = (nextOrgId: string) => {
    const nextMembership = memberships.find((m) => m.organizationId === nextOrgId);
    if (!nextMembership) {
      setActiveOrgId(null);
      return;
    }
    setActiveOrgId(nextMembership.organizationId);

    const currentPath = location.pathname;
    const orgMatch = currentPath.match(/^\/o\/[^/]+(\/.*)?$/);
    if (orgMatch) {
      const remainder = orgMatch[1] ?? '/dashboard';
      navigate(`/o/${nextMembership.organizationSlug}${remainder}`, { replace: true });
    } else {
      navigate(`/o/${nextMembership.organizationSlug}/dashboard`);
    }
  };

  const memberships = user?.memberships ?? [];
  const active =
    memberships.find((m) => m.organizationId === activeOrgId) ?? memberships[0] ?? null;

  const handleLogout = () => {
    clear();
    success('Signed out', 'You have been signed out of your account');
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 items-center gap-3 border-b bg-background px-4">
      {memberships.length > 1 && !user?.isSuperAdmin ? (
        <label className="flex items-center gap-2 text-sm">
          <span className="sr-only">Active organization</span>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            value={active?.organizationId ?? ''}
            onChange={(e) => handleOrgSwitch(e.target.value)}
          >
            {memberships.map((m) => (
              <option key={m.organizationId} value={m.organizationId}>
                {m.organizationName}
              </option>
            ))}
          </select>
        </label>
      ) : active ? (
        <div className="text-sm font-medium text-foreground/90">{active.organizationName}</div>
      ) : null}

      <div className="relative hidden min-w-0 flex-1 sm:block">
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(
              new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }),
            );
          }}
          className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background pl-3 pr-3 text-left text-sm text-muted-foreground shadow-sm hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Open search"
        >
          <Search className="h-4 w-4" aria-hidden />
          <span className="flex-1 truncate">Search issues, projects, people…</span>
          <kbd className="hidden rounded border bg-muted/50 px-1.5 py-0.5 text-[10px] md:inline">
            Ctrl+K
          </kbd>
        </button>
      </div>

      <Button variant="ghost" size="icon" aria-label="Notifications">
        <Bell className="h-4 w-4" aria-hidden />
      </Button>

      <div className="flex items-center gap-3 pl-2">
        <UserAvatar
          name={user?.name ?? null}
          email={user?.email ?? null}
          avatarAttachmentId={user?.avatarAttachmentId ?? null}
          size={32}
        />
        <div className="hidden flex-col text-xs leading-tight md:flex">
          <span className="font-medium text-foreground">{user?.name ?? 'Guest'}</span>
          {active ? (
            <span className="text-muted-foreground">{active.roleName}</span>
          ) : user?.isSuperAdmin ? (
            <span className="text-muted-foreground">Super admin</span>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </header>
  );
}
